'use client';

import { ReactNode, useId, useState } from 'react';

import { useTranslations } from 'next-intl';

import { findCurrentStepId } from '@/components/assistant-ui/thread/activity-trail-helpers';
import type { TrailIconRenderer } from '@/components/assistant-ui/thread/rotating-trail-icon';
import type { TimelineRow } from '@/components/assistant-ui/thread/trail-records';
import { TrailRow } from '@/components/assistant-ui/thread/trail-row';
import { useTrailCollapseMode } from '@/components/assistant-ui/thread/use-trail-collapse-mode';
import { MESSAGE_TEXT_SCALE } from '@/components/ui/type-scale';
import { cn } from '@/lib/utils';

export const TRAIL_PANEL_ATTR = 'data-trail-panel';

/**
 * React key for a row. `morphKey` (when set by `buildTrailTimeline`) claims
 * a stable `gap:N` slot — so a record that morphs into a trailing thinking
 * row's slot inherits the same key and React preserves the same `TrailRow`
 * instance, letting the icon + text crossfades inside fire for the morph.
 * Falls back to `row.id` for rows that don't claim a slot (the always-on
 * thinking header).
 */
export function rowReactKey(row: TimelineRow): string {
  return row.morphKey ?? row.id;
}

export function CollapsingTrail({
  rows,
  isLive,
  waitingOnGuest = false,
  renderInProgressIcon,
  renderStepTrailing,
  timerNode,
  expanded: controlledExpanded,
}: {
  rows: readonly TimelineRow[];
  isLive: boolean;
  /** The run is parked on the guest: not streaming, but not finished either, so it stays open. */
  waitingOnGuest?: boolean;
  renderInProgressIcon: TrailIconRenderer;
  renderStepTrailing?: (row: TimelineRow) => ReactNode | undefined;
  timerNode: ReactNode;
  // Forced expansion state. When provided, overrides internal toggle/auto-
  // collapse logic — useful for previews and demos that need a deterministic
  // open/closed state (e.g. Storybook frames).
  expanded?: boolean;
}) {
  const t = useTranslations();
  const panelId = useId();
  const { renderPanelMode, expanded, toggle } = useTrailCollapseMode({
    isLive: isLive || waitingOnGuest,
    controlledExpanded,
  });
  // Decided when the open trail appears, never while it shows: re-adding the class to a mounted
  // trail replays the fade, as a run resuming from a gate would.
  const [fadesIn, setFadesIn] = useState(isLive);
  const [openShown, setOpenShown] = useState(!renderPanelMode);
  if (openShown === renderPanelMode) {
    setOpenShown(!renderPanelMode);
    if (!renderPanelMode) setFadesIn(isLive);
  }
  // Opened on a run already parked (a thread switched to, a reload), the trail shows the state it
  // reached: only the steps the resumed run adds slide in.
  const [rowsAtParkedOpen] = useState(() =>
    !isLive && waitingOnGuest ? new Set(rows.map(rowReactKey)) : undefined,
  );

  if (rows.length === 0) return null;

  const lastIdx = rows.length - 1;
  const currentRowId = findCurrentStepId(rows);
  // Panel mode lives in a different DOM hierarchy and forces a remount —
  // suppress the slide-in keyframe so the rows don't all flash in.
  const renderRow = (row: TimelineRow, opts: { showChevron: boolean }) => (
    <TrailRow
      key={rowReactKey(row)}
      row={row}
      expanded={expanded}
      isCurrent={row.id === currentRowId}
      showChevron={opts.showChevron}
      renderInProgressIcon={renderInProgressIcon}
      renderTrailing={renderStepTrailing}
      skipSlideIn={renderPanelMode || !!rowsAtParkedOpen?.has(rowReactKey(row))}
    />
  );

  if (!renderPanelMode) {
    return (
      <div
        style={MESSAGE_TEXT_SCALE}
        className={cn(
          'aui-progress-tracker -mx-3 my-2 w-[calc(100%+1.5rem)] rounded-md select-none',
          fadesIn && 'motion-safe:animate-in motion-safe:duration-500 motion-safe:fade-in',
        )}
      >
        <ol className="flex flex-col">{rows.map(row => renderRow(row, { showChevron: false }))}</ol>
        {timerNode}
      </div>
    );
  }

  const canToggle = rows.length > 1;
  const panelRows = rows.slice(0, lastIdx);
  const toggleRow = rows[lastIdx];
  const toggleLabel = expanded ? t('activityTrail.collapse') : t('activityTrail.expand');

  return (
    <div
      style={MESSAGE_TEXT_SCALE}
      className={cn(
        'aui-progress-tracker relative -mx-3 my-2 w-[calc(100%+1.5rem)] rounded-md transition-colors select-none',
        canToggle && 'hover:bg-muted/40',
      )}
    >
      {canToggle && (
        // Laid over the whole trail rather than wrapping it: a button's content is presentational,
        // so wrapped, the steps would never reach a screen reader.
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={panelId}
          aria-label={toggleLabel}
          onClick={toggle}
          className="absolute inset-0 z-10 cursor-pointer rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      )}
      {panelRows.length > 0 && (
        // CSS grid `1fr → 0fr` collapsible: pure CSS transition, no JS in
        // the animation loop. Opacity animates alongside the height so rows
        // fade out as they shrink instead of guillotining at full alpha.
        <div
          className={cn(
            'grid transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
            expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
          )}
          id={panelId}
          aria-hidden={!expanded}
          data-trail-panel=""
        >
          <div className="overflow-hidden">
            <ol className="flex flex-col">
              {panelRows.map(row => renderRow(row, { showChevron: false }))}
            </ol>
          </div>
        </div>
      )}
      <ol className="flex flex-col">{renderRow(toggleRow, { showChevron: canToggle })}</ol>
      {timerNode}
    </div>
  );
}
